import React, { useEffect, useState } from "react";
import { Shield, UserPlus, Send, RotateCcw, Copy, Check, Trash2, ArrowLeft, Clock, Ban, Layers } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDate, formatDateTime, USER_STATUS_LABELS, userStatusTone } from "../lib/constants";
import { useAuth } from "../lib/auth";

export default function ClientTeam() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [manageAccessMember, setManageAccessMember] = useState(null);
  const location = useLocation();
  const [showInvite, setShowInvite] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("invite") === "1";
  });

  const load = async () => {
    try {
      const { data } = await api.get("/client/team");
      setState(data);
    } catch (e) { setState({ team: [], cap: 999, count: 0, error: formatApiErrorDetail(e.response?.data?.detail) || e.message }); }
  };
  useEffect(() => { load(); }, []);

  if (!state) return <div className="max-w-5xl mx-auto px-6 py-16 label">Loading…</div>;

  const canInvite = true; // cap removed — unlimited users

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 fade-up" data-testid="client-team-page">
      <Link to="/portal" className="inline-flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] mb-6">
        <ArrowLeft size={14} /> Back to projects
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2 flex items-center gap-2">
            <Shield size={12} /> Client administrator
          </div>
          <h1 className="font-display text-4xl tracking-tight text-[color:var(--atech-text)]">Your team</h1>
          <p className="mt-2 text-sm text-[color:var(--atech-text-dim)] max-w-xl">
            Invite teammates into your portal workspace. They'll receive an activation link to set their own password.
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} disabled={!canInvite} className="btn-copper text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="invite-teammate-button">
          <UserPlus size={14} /> Invite teammate
        </button>
      </div>

      <div className="text-xs text-[color:var(--atech-text-muted)] mb-4">{state.count} team member{state.count !== 1 ? "s" : ""}</div>
      {state.error && <div className="text-sm text-[#F87171] mb-4">{state.error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {state.team.map((m) => (
          <TeamCard key={m.id} member={m} me={user} onReload={load} onManageAccess={() => setManageAccessMember(m)} />
        ))}
      </div>

      {showInvite && (
        <InviteTeammateModal
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); load(); }}
        />
      )}
      {manageAccessMember && (
        <ManageClientProjectAccessModal
          member={manageAccessMember}
          onClose={() => setManageAccessMember(null)}
        />
      )}
    </div>
  );
}

function TeamCard({ member, me, onReload, onManageAccess }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const status = member.status || "active";
  const isSelf = member.id === me?.id;
  const isMeAdmin = me?.permission === "edit" || me?.client_role === "admin";

  const resend = async () => {
    setBusy(true); setErr(""); setLink(""); setCopied(false);
    try {
      const { data } = await api.post(`/client/team/${member.id}/resend-invite`);
      setLink(data.activation_url); onReload();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm(`Remove ${member.name} from the team?`)) return;
    setBusy(true);
    try { await api.delete(`/client/team/${member.id}`); onReload(); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="card-surface p-5 flex flex-col gap-3" data-testid={`team-card-${member.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-lg text-[color:var(--atech-text)] leading-tight truncate flex items-center gap-2">
            {member.name}
            {isSelf && <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)]">You</span>}
          </div>
          <div className="text-xs text-[color:var(--atech-text-dim)] mt-0.5 break-all">{member.email}</div>
          {(member.permission === "edit" || member.client_role === "admin") && (
            <div className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-copper)]">
              <Shield size={10} /> {member.permission === "edit" ? "Edit access" : "Client admin"}
            </div>
          )}
        </div>
        {!isSelf && (
          <button onClick={remove} className="text-[color:var(--atech-text-muted)] hover:text-[#F87171] shrink-0"><Trash2 size={14} /></button>
        )}
      </div>
      <span className={`inline-flex self-start items-center px-2.5 py-1 rounded-[2px] text-[10px] uppercase tracking-[0.16em] border ${userStatusTone(status)}`}>
        {USER_STATUS_LABELS[status] || status}
      </span>
      <dl className="space-y-1 text-[11px] text-[color:var(--atech-text-muted)]">
        {member.invite_sent_at && <div className="flex items-center gap-2"><Clock size={10} /> Invited {formatDate(member.invite_sent_at)}</div>}
        {member.invite_accepted_at && <div className="flex items-center gap-2"><Check size={10} /> Activated {formatDate(member.invite_accepted_at)}</div>}
        {member.last_login_at && <div>Last login {formatDateTime(member.last_login_at)}</div>}
      </dl>
      {link && (
        <div className="text-[11px] bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] p-2.5 flex items-center gap-2">
          <input readOnly value={link} className="flex-1 bg-transparent outline-none text-[color:var(--atech-text)] font-mono text-[10px] truncate" />
          <button onClick={copy} className="text-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper-hover)] flex items-center gap-1 shrink-0">
            {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
        </div>
      )}
      {err && <div className="text-[11px] text-[#F87171]">{err}</div>}
      {status !== "active" && status !== "disabled" && !isSelf && (
        <button onClick={resend} disabled={busy} className="mt-auto text-[11px] self-start px-2.5 py-1.5 rounded-[2px] border border-[color:var(--atech-copper)] text-[color:var(--atech-copper)] hover:bg-[rgba(197,116,66,0.08)] flex items-center gap-1.5" data-testid={`resend-team-${member.id}`}>
          <RotateCcw size={11} /> Resend invite
        </button>
      )}
      {isMeAdmin && !isSelf && (
        <button
          onClick={onManageAccess}
          className="text-[11px] px-2.5 py-1.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-copper)] hover:border-[color:var(--atech-copper)]/50 flex items-center gap-1.5 w-full mt-1"
          data-testid={`manage-projects-${member.id}`}
        >
          <Layers size={11} /> Manage project access
        </button>
      )}
    </div>
  );
}

function InviteTeammateModal({ onClose, onInvited }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/client/team/invite", form);
      setLink(data.activation_url);
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface w-full max-w-md p-8 fade-up" onClick={(e) => e.stopPropagation()} data-testid="invite-teammate-modal">
        <h3 className="font-display text-xl text-[color:var(--atech-text)] mb-5">Invite a teammate</h3>
        {!link ? (
          <form onSubmit={submit} className="space-y-4" data-testid="invite-teammate-form">
            <div><span className="label block mb-2">Name</span><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="invite-teammate-name" /></div>
            <div><span className="label block mb-2">Email</span><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="invite-teammate-email" /></div>
            <p className="text-[11px] text-[color:var(--atech-text-muted)]">
              New teammates join as <span className="text-[color:var(--atech-text)]">Client members</span> — they can view
              documents you have access to but can't invite others.
            </p>
            {err && <div className="text-xs text-[#F87171]">{err}</div>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
              <button type="submit" disabled={busy} className="btn-copper text-sm flex items-center gap-2" data-testid="invite-teammate-submit">
                <Send size={13} /> {busy ? "Sending…" : "Send invite"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-[color:var(--atech-text)]">Invite ready. Copy the link below and share it with your teammate.</div>
            <div className="text-[11px] bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] p-3 flex items-center gap-2">
              <input readOnly value={link} className="flex-1 bg-transparent outline-none text-[color:var(--atech-text)] font-mono text-[10px] truncate" />
              <button onClick={() => navigator.clipboard.writeText(link)} className="text-[color:var(--atech-copper)] text-xs flex items-center gap-1"><Copy size={11} /> Copy</button>
            </div>
            <div className="flex justify-end">
              <button onClick={onInvited} className="btn-copper text-sm">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ManageClientProjectAccessModal({ member, onClose }) {
  const [projects, setProjects] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/client/team/${member.id}/project-access`)
      .then(({ data }) => {
        setProjects(data.projects);
        setSelected(new Set(data.projects.filter((p) => p.has_access).map((p) => p.id)));
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [member.id]);

  const toggle = (pid) => setSelected((prev) => {
    const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n;
  });

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/client/team/${member.id}/project-access`, {
        project_ids: Array.from(selected),
      });
      onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface w-full max-w-md p-8 fade-up" onClick={(e) => e.stopPropagation()} data-testid="client-manage-projects-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-[color:var(--atech-text)]">Project access — {member.name.split(" ")[0]}</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]">✕</button>
        </div>
        <div className="space-y-4">
          {err && <div className="text-xs text-[#F87171]">{err}</div>}
          {!projects ? (
            <div className="label text-[color:var(--atech-text-muted)]">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="text-sm text-[color:var(--atech-text-dim)]">No projects found you have access to.</div>
          ) : (
            <>
              <div className="text-xs text-[color:var(--atech-text-muted)]">
                Choose which projects {member.name.split(" ")[0]} can access. You can only manage access to projects you are on.
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto border border-[color:var(--atech-border)] rounded-[2px] p-2">
                {projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2.5 px-1 py-1.5 rounded cursor-pointer hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="accent-[color:var(--atech-copper)] shrink-0"
                      data-testid={`client-access-project-${p.id}`}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="text-xs text-[color:var(--atech-text)] truncate block">{p.project_name}</span>
                      {p.project_reference && (
                        <span className="text-[10px] font-mono text-[color:var(--atech-text-muted)]">{p.project_reference}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-[color:var(--atech-text-muted)]">{selected.size} project{selected.size !== 1 ? "s" : ""} selected</div>
            </>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button onClick={save} disabled={busy || !projects} className="btn-copper text-sm" data-testid="client-save-project-access">
              {busy ? "Saving…" : "Save access"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
